import React, { useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import { ProTablePlus } from '@bedrock/eed-react';
import { IProTablePlusColumn } from '@bedrock/eed-react/lib/components/ProTablePlus/interface';
import { SupplierRequestInvoker, FormItemBaseFile, FormModule, supplierDefs, supplierEnums } from '@purchase/supplier';
import NoData from '@bedrock/components/es/Empty/images/small/NoData';
import dayjs from 'dayjs';
import './ProxyListTable.less';

export interface ProxyListTableProps {
  vendorId: string;
  className?: string;
  style?: React.CSSProperties;
}

export function ProxyListTable(props: ProxyListTableProps) {
  const { vendorId, className, style } = props;

  // 筛选条件 state（前端单选筛选）
  const [selectedPlatform, setSelectedPlatform] = useState<string>('');
  const [selectedProxyType, setSelectedProxyType] = useState<string>('');

  // 一次性加载全量数据
  const { data, loading } = useRequest(
    () => SupplierRequestInvoker.getInstance().vendorQueryVendorProxyList({ vendorId }),
    { refreshDeps: [vendorId] },
  );

  // 从返回数据提取列表和更新时间
  const allData: supplierDefs.VendorProxyDataListDto[] = useMemo(
    () => (data?.data as supplierDefs.VendorProxyDataListDto[]) || [],
    [data],
  );
  const updateTime = useMemo(() => {
    if (allData.length === 0) return '';
    const timestamps = allData
      .map((item) => item.crawlerTimestamp)
      .filter(Boolean) as (string | number)[];
    if (timestamps.length === 0) return '';
    const latest = timestamps.reduce((a, b) => (dayjs(a).isAfter(dayjs(b)) ? a : b));
    return dayjs(latest).format('YYYY-MM-DD HH:mm:ss');
  }, [allData]);

  // 平台筛选选项：根据列表中实际存在的平台值动态生成
  const platformOptions = useMemo(
    () =>
      [...new Set(allData.map((item) => item.platform).filter(Boolean) as string[])].map(
        (v) => ({
          name: supplierEnums.VendorProxyPlatformEnum.pick(v)?.name || v,
          value: v,
        }),
      ),
    [allData],
  );
  const proxyTypeOptions = useMemo(
    () =>
      [...new Set(allData.map((item) => item.agentType).filter(Boolean) as string[])].map(
        (v) => ({ name: v, value: v }),
      ),
    [allData],
  );
  // 前端过滤数据
  const displayData = useMemo(
    () =>
      allData.filter((item) => {
        const matchPlatform = !selectedPlatform || item.platform === selectedPlatform;
        const matchProxyType = !selectedProxyType || item.agentType === selectedProxyType;
        return matchPlatform && matchProxyType;
      }),
    [allData, selectedPlatform, selectedProxyType],
  );

  const columns: IProTablePlusColumn[] = useMemo(
    () => [
      {
        title: '#',
        dataIndex: 'rank',
        width: 50,
        fixed: 'left' as const,
        render: (_: unknown, __: unknown, index: number) => index + 1,
      },
      {
        title: '平台名称',
        dataIndex: 'platform',
        width: 200,
        filterRender: {
          name: 'select',
          props: { options: platformOptions, multiple: false },
        },
        render: (value: string) => (
          <span className="font-bold">{supplierEnums.VendorProxyPlatformEnum.pick(value).name || '-'}</span>
        ),
      },
      {
        title: '代理范围',
        dataIndex: 'proxyScope',
        render: (value: string) => <span>{value || '-'}</span>,
      },
      {
        title: '代理类型',
        dataIndex: 'agentType',
        width: 200,
        filterRender: {
          name: 'select',
          props: { options: proxyTypeOptions, multiple: false },
        },
        render: (value: string) => <span>{value || '-'}</span>,
      },
      {
        title: '有效期',
        dataIndex: 'validityPeriod',
        width: 200,
        render: (value: string) => <span>{value || '-'}</span>,
      },
      {
        title: '授权证书',
        dataIndex: 'authCertFile',
        width: 240,
        render: (value: unknown) => {
          if (!value || (Array.isArray(value) && value.length === 0)) {
            return '-';
          }
          return (
            <FormItemBaseFile
              label=""
              value={value}
              disabled
              fieldProps={{ className: 'w-[240px]' }}
            />
          );
        },
      },
    ],
    [platformOptions, proxyTypeOptions],
  );

  // ProTablePlus onFilterChange 回调：接收包含所有列筛选条件的对象
  const handleFilterChange = (filterItem: string,filterValue: Record<string, string>) => {
    setSelectedPlatform(filterValue.platform ?? '');
    setSelectedProxyType(filterValue.agentType ?? '');
  };

  return (
    loading ? null : <FormModule
      title="代理清单"
      titleCount={allData.length}
      proCardProps={{
        expandable: true,
        defaultExpanded: !!allData.length,
        rightSlot:
          allData.length > 0 ? (
            <div className="flex items-center gap-3">
              <span className="text-color-3 text-xs">*数据来源于媒体平台</span>
              {updateTime && (
                <span className="text-color-3 text-xs">更新时间：{updateTime}</span>
              )}
            </div>
          ) : null,
      }}
    >
      <div className={`proxy-list-table${className ? ` ${className}` : ''}`} style={style}>
        <ProTablePlus
          columns={columns}
          data={displayData}
          loading={loading}
          scroll={{ x: true }}
          pagination={false}
          onFilterChange={handleFilterChange}
          emptyText={
            <div className="rock-empty">
              <NoData className="rock-empty-image" />
              <div className="rock-empty-text">当前数据为空</div>
            </div>
          }
        />
      </div>
    </FormModule>
  );
}